<main>
  <p-confirmDialog key="rv-dialog" styleClass="rv-confirm-dialog">
    <ng-template #headless let-message let-onAccept="onAccept" let-onReject="onReject">
      <div class="flex flex-col items-center p-8 bg-white rounded-xl w-full max-w-lg mx-auto shadow-xl">
        <div class="rounded-full bg-amber-500 text-white inline-flex justify-center items-center h-24 w-24 -mt-20 shadow-lg">
          <i class="pi pi-exclamation-triangle text-5xl"></i>
        </div>

        <span class="font-bold text-2xl text-center mt-6 mb-2 text-gray-800">
          {{ message.header }}
        </span>

        <p class="text-center text-xl leading-relaxed text-gray-700" [innerHTML]="message.message"></p>

        <div class="flex justify-center gap-6 mt-8 w-full">
          <p-button [label]="message.rejectLabel" (click)="onReject()" variant="outlined" styleClass="cs-button-touch" />
          <p-button [label]="message.acceptLabel" (click)="onAccept()" styleClass="cs-button-touch" />
        </div>
      </div>
    </ng-template>
  </p-confirmDialog>
  @if (isLoading()) {
    <loading-overlay [isLoading]="true"></loading-overlay>
  } @else {
    <header>
      <div>
        <h1>{{ cycle()?.startStationAssetId }} - (Cycle #{{ cycle()?.cycleNumber }})</h1>
        @if (recipeValues()) {
          <p-tag
            [value]="recipeValues()!.status === RecipeValuesStatus.Completed ? 'Completed' : 'In Progress'"
            [severity]="recipeValues()!.status === RecipeValuesStatus.Completed ? 'success' : 'warn'"
          />
        }
      </div>
      <div class="buttons">
        @if (isRecipeAssociated() && recipeValues()) {
          <p-button
            label="Save Draft"
            icon="pi pi-save"
            severity="secondary"
            [outlined]="true"
            [loading]="isSaving()"
            [disabled]="recipeValues()!.status === RecipeValuesStatus.Completed || !canEdit()"
            (onClick)="saveDraft()"
          />
          <p-button
            label="Mark as Complete"
            icon="pi pi-check-circle"
            severity="success"
            [loading]="isSaving()"
            [disabled]="recipeValues()!.status === RecipeValuesStatus.Completed || !waterConfirmed() || !allRecipeLinesConfirmed() || hasInsufficientInventory() || !canEdit()"
            pTooltip="{{ !waterConfirmed() ? 'Confirm water usage before completing' : !allRecipeLinesConfirmed() ? 'Confirm all recipe lines before completing' : '' }}"
            tooltipPosition="bottom"
            (onClick)="markAsComplete()"
          />
        }
        <p-button label="Back" variant="outlined" (onClick)="goBack()" />
      </div>
    </header>

    <div class="content">
      <!-- Chemical inventory consumption notice -->
      @if (!recipeValues() || recipeValues()!.status !== RecipeValuesStatus.Completed) {
        <div class="consumption-notice mb-3">
          <p-message severity="info" styleClass="w-full">{{ getConsumptionNoticeMessage() }}</p-message>
        </div>
      }

      <!-- Cycle Information -->
      @if (cycle()) {
        <p-panel header="Cycle Information">
          <p-card>
            <div class="grid">
              <div class="col-12 md:col-6">
                <p><strong>Cycle:</strong> {{ cycle()!.startStationAssetId }} - (Cycle #{{ cycle()!.cycleNumber }})</p>
                <p><strong>Production Card:</strong> {{ productionCardNumber() }}</p>
              </div>
              <div class="col-12 md:col-6">
                <p><strong>Start Time:</strong> {{ cycle()!.startTime | date: 'short' }}</p>
                <p><strong>End Time:</strong> {{ cycle()!.endTime | date: 'short' }}</p>
              </div>
            </div>
          </p-card>
        </p-panel>
      }

      <!-- Recipe Selection (if not yet associated) -->
      @if (!isRecipeAssociated()) {
        <p-panel header="Recipe Selection">
          <p-card>
            <form [formGroup]="recipeForm">
              <div class="flex gap-3 align-items-end">
                <div class="flex-1">
                  <label for="recipeSelect" class="block mb-2">Select Recipe:</label>
                  <p-select id="recipeSelect" [options]="availableRecipes()" formControlName="recipeReference" optionValue="id" placeholder="Select a recipe" styleClass="w-full">
                    <ng-template pTemplate="selectedItem" let-recipe>
                      {{ getRecipeLabel(recipe) }}
                    </ng-template>
                    <ng-template pTemplate="item" let-recipe>
                      {{ getRecipeLabel(recipe) }}
                    </ng-template>
                  </p-select>
                </div>
                <div>
                  <label class="block mb-2">&nbsp;</label>
                  <p-button
                    label="Associate Recipe"
                    icon="pi pi-check"
                    [loading]="isSaving()"
                    [disabled]="!recipeForm.get('recipeReference')?.value || (recipePreview() && !hasProcessesInPreview()) || !canEdit()"
                    (onClick)="associateRecipe()"
                  />
                </div>
              </div>
            </form>
          </p-card>
        </p-panel>
      }

      <!-- Recipe Preview (shown when recipe is selected but not yet associated) -->
      @if (!isRecipeAssociated() && recipePreview()) {
        <p-panel header="Recipe Preview">
          <p-card>
            <div class="recipe-header-info mb-4">
              <div class="grid">
                <div class="col-12 md:col-4">
                  <p><strong>Recipe:</strong> {{ recipePreview()!.recipeNumber }}</p>
                </div>
                @if (recipePreview()!.liquorRatio) {
                  <div class="col-12 md:col-4">
                    <p><strong>Liquor Ratio:</strong> 1:{{ recipePreview()!.liquorRatio }}</p>
                  </div>
                }
                @if (recipePreview()!.shadeDto?.name) {
                  <div class="col-12 md:col-4">
                    <p><strong>Shade:</strong> {{ recipePreview()!.shadeDto!.name }}</p>
                  </div>
                }
              </div>
            </div>

            <div class="preview-info mb-3">
              @if (hasProcessesInPreview()) {
                <p-message severity="info" styleClass="w-full"> This is a preview of the recipe. Click "Associate Recipe" to save and start entering actual values. </p-message>
              } @else {
                <p-message severity="warn" styleClass="w-full"> This recipe does not contain any processes or items. You cannot associate a recipe without processes. </p-message>
              }
            </div>
          </p-card>

          <!-- Process Groups Preview -->
          @for (processGroup of getPreviewProcessGroups(); track processGroup.processId; let processIdx = $index) {
            <div class="process-group mb-4">
              <p-panel data-tone="surface" [header]="processGroup.processName">
                <p-card data-variant="compact">
                  <p-table [value]="processGroup.items" [scrollable]="true" scrollHeight="400px" styleClass="cs-datatable-editable">
                    <ng-template pTemplate="header">
                      <tr>
                        <th style="width: 100px">Step</th>
                        <th style="width: 150px">Item Code</th>
                        <th style="width: 220px">{{ 'Recipe_Values_Item_Description' | translateWF: 'Item Description' }}</th>
                        <th style="width: 120px">{{ 'Recipe_Values_Estimated_Kg' | translateWF: 'Estimated (Kg)' }}</th>
                        <th style="width: 80px">Unit</th>
                        <th style="width: 150px">{{ 'Recipe_Values_Current_Inventory' | translateWF: 'Current Inventory' }}</th>
                      </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-item>
                      <tr>
                        <td>{{ item.stepOrder }}</td>
                        <td>{{ item.itemCode }}</td>
                        <td>{{ item.itemDescription || '--' }}</td>
                        <td>
                          @if (calculatePreviewEstimatedKg(item.estimatedQuantity, item.unit) !== null) {
                            {{ calculatePreviewEstimatedKg(item.estimatedQuantity, item.unit) | number: '1.0-5' }}
                          } @else {
                            --
                          }
                        </td>
                        <td>{{ item.unit }}</td>
                        <td>{{ item.currentInventory != null ? (item.currentInventory | number: '1.0-5') : '--' }}</td>
                      </tr>
                    </ng-template>
                  </p-table>
                </p-card>
              </p-panel>
            </div>
          }
        </p-panel>
      }

      <!-- Recipe Values (shown after recipe is associated) -->
      @if (isRecipeAssociated() && recipeValues() && selectedRecipe()) {
        <p-panel>
          <p-card>
            <div class="recipe-header-info mb-4">
              <div class="grid">
                <div class="col-12 md:col-4">
                  <p><strong>Recipe:</strong> {{ recipeValues()!.recipeNumber }}</p>
                </div>
                @if (selectedRecipe()!.liquorRatio) {
                  <div class="col-12 md:col-4">
                    <p><strong>Liquor Ratio:</strong> 1:{{ selectedRecipe()!.liquorRatio }}</p>
                  </div>
                }
                <div class="col-12 md:col-4">
                  <p><strong>Bundle Weight (kg):</strong> {{ calculateKgTela() | number: '1.0-5' }}</p>
                </div>
                @if (selectedRecipe()!.shadeDto?.name) {
                  <div class="col-12 md:col-4">
                    <p><strong>Shade:</strong> {{ selectedRecipe()!.shadeDto!.name }}</p>
                  </div>
                }
              </div>
            </div>

            @if (hasInsufficientInventory()) {
              <div class="insufficient-inventory-warning mb-4">
                <p-message severity="warn" styleClass="w-full">
                  <div>
                    <strong>{{ 'Recipe_Values_Insufficient_Inventory_Title' | translateWF: 'Insufficient inventory for the following items:' }}</strong>
                    <ul>
                      @for (item of getInsufficientInventoryItems(); track item.itemCode) {
                        <li>
                          {{ item.itemCode }} — {{ item.itemDescription }}: {{ 'Recipe_Values_Insufficient_Inventory_Needs' | translateWF: 'needs' }}
                          {{ item.required | number: '1.0-5' }} kg, {{ 'Recipe_Values_Insufficient_Inventory_Available' | translateWF: 'available' }}
                          {{ item.available | number: '1.0-5' }} kg
                        </li>
                      }
                    </ul>
                  </div>
                </p-message>
              </div>
            }

            <!-- Water Usage Field -->
            <div class="water-usage-field mb-4 flex align-items-center gap-2">
              <label for="waterUsage"><strong>Consumo de agua (L):</strong></label>
              <input
                id="waterUsage"
                type="number"
                pInputText
                [value]="waterUsageInLiters() ?? 0"
                (blur)="onWaterUsageBlur(+$any($event.target).value)"
                step="0.01"
                min="0"
                style="width: 160px"
                [disabled]="recipeValues()!.status === RecipeValuesStatus.Completed || waterConfirmed()"
              />
              @if (!waterConfirmed()) {
                <p-button
                  icon="pi pi-check"
                  severity="success"
                  [text]="true"
                  size="small"
                  pTooltip="Confirm value"
                  tooltipPosition="top"
                  (onClick)="confirmWater()"
                  [disabled]="recipeValues()!.status === RecipeValuesStatus.Completed"
                />
              } @else {
                <p-button
                  icon="pi pi-pencil"
                  severity="secondary"
                  [text]="true"
                  size="small"
                  pTooltip="Change value"
                  tooltipPosition="top"
                  (onClick)="unconfirmWater()"
                  [disabled]="recipeValues()!.status === RecipeValuesStatus.Completed"
                />
              }
            </div>
          </p-card>

          <form [formGroup]="recipeForm">
            <div formArrayName="actualValues">
              <!-- Group by Process -->
              @for (processGroup of getProcessGroups(); track processGroup.processId; let processIdx = $index) {
                <div class="process-group mb-4">
                  <p-panel data-tone="surface" [header]="'Process ' + (processIdx + 1)">
                    <p-card data-variant="compact">
                      <p-table [value]="processGroup.entries" [scrollable]="true" scrollHeight="400px" styleClass="cs-datatable-editable">
                        <ng-template pTemplate="header">
                          <tr>
                            <th style="width: 100px">Step</th>
                            <th style="width: 150px">Item Code</th>
                            <th style="width: 220px">{{ 'Recipe_Values_Item_Description' | translateWF: 'Item Description' }}</th>
                            <th style="width: 120px">Estimated</th>
                            <th style="width: 80px">Unit</th>
                            <th style="width: 140px">{{ 'Recipe_Values_Estimated_Kg' | translateWF: 'Estimated (Kg)' }}</th>
                            <th style="width: 175px">Actual (Kg)</th>
                            <th style="width: 160px">Variance</th>
                            <th style="width: 140px">{{ 'Recipe_Values_Current_Inventory' | translateWF: 'Current Inventory' }}</th>
                            <th>Notes</th>
                          </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-formGroup let-rowIndex="rowIndex">
                          @for (entryFormGroup of actualValuesFormArray.controls; track $index; let i = $index) {
                            @if (entryFormGroup === formGroup) {
                              <tr [formGroupName]="i">
                                <td>{{ formGroup.controls.stepOrder.value }}</td>
                                <td>{{ formGroup.controls.itemCode.value }}</td>
                                <td>{{ formGroup.controls.itemDescription.value }}</td>
                                <td>{{ formGroup.controls.estimatedQuantity.value | number: '1.2-2' }}</td>
                                <td>{{ formGroup.controls.unit.value }}</td>
                                <td>
                                  @if (calculateEstimatedKg(formGroup.controls.estimatedQuantity.value, formGroup.controls.unit.value) !== null) {
                                    {{ calculateEstimatedKg(formGroup.controls.estimatedQuantity.value, formGroup.controls.unit.value) | number: '1.0-5' }}
                                  } @else {
                                    --
                                  }
                                </td>
                                <td>
                                  <div class="flex align-items-center gap-1">
                                    <input
                                      type="number"
                                      pInputText
                                      formControlName="actualKg"
                                      step="0.00001"
                                      min="0"
                                      class="w-full"
                                      [class.input-error]="formGroup.controls.actualKg.value !== null && formGroup.controls.actualKg.value! < 0"
                                    />
                                    @if (!formGroup.controls.confirmed.value) {
                                      <p-button
                                        icon="pi pi-check"
                                        severity="success"
                                        [text]="true"
                                        size="small"
                                        pTooltip="Confirm value"
                                        tooltipPosition="top"
                                        (onClick)="confirmLine(formGroup)"
                                      />
                                    } @else {
                                      <p-button
                                        icon="pi pi-pencil"
                                        severity="secondary"
                                        [text]="true"
                                        size="small"
                                        pTooltip="Change value"
                                        tooltipPosition="top"
                                        (onClick)="unconfirmLine(formGroup)"
                                        [disabled]="recipeValues()!.status === RecipeValuesStatus.Completed"
                                      />
                                    }
                                  </div>
                                  @if (formGroup.controls.actualKg.value !== null && formGroup.controls.actualKg.value! < 0) {
                                    <small class="error-text">Cannot be negative</small>
                                  }
                                </td>
                                <td>
                                  <div class="variance-container">
                                    <span
                                      [class.cs-value-positive]="
                                        formGroup.controls.actualKg.value !== null &&
                                        formGroup.controls.actualKg.value! >= (calculateEstimatedKg(formGroup.controls.estimatedQuantity.value, formGroup.controls.unit.value) ?? 0)
                                      "
                                      [class.cs-value-negative]="
                                        formGroup.controls.actualKg.value !== null &&
                                        formGroup.controls.actualKg.value! < (calculateEstimatedKg(formGroup.controls.estimatedQuantity.value, formGroup.controls.unit.value) ?? 0)
                                      "
                                      [class.variance-high]="
                                        hasHighVariance(
                                          calculateEstimatedKg(formGroup.controls.estimatedQuantity.value, formGroup.controls.unit.value),
                                          formGroup.controls.actualKg.value
                                        )
                                      "
                                    >
                                      {{
                                        calculateVariance(
                                          calculateEstimatedKg(formGroup.controls.estimatedQuantity.value, formGroup.controls.unit.value),
                                          formGroup.controls.actualKg.value
                                        )
                                      }}
                                    </span>
                                    @if (
                                      hasHighVariance(
                                        calculateEstimatedKg(formGroup.controls.estimatedQuantity.value, formGroup.controls.unit.value),
                                        formGroup.controls.actualKg.value
                                      )
                                    ) {
                                      <i class="pi pi-exclamation-triangle variance-warning-icon" pTooltip="Variance exceeds 20%"></i>
                                    }
                                  </div>
                                </td>
                                <td>
                                  {{ formGroup.controls.currentInventory.value != null ? (formGroup.controls.currentInventory.value | number: '1.0-5') : '--' }}
                                </td>
                                <td>
                                  <input type="text" pInputText formControlName="notes" class="w-full" />
                                </td>
                              </tr>
                            }
                          }
                        </ng-template>
                      </p-table>
                    </p-card>
                  </p-panel>
                </div>
              }

              <!-- Additional Items (manual) -->
              <div class="process-group mb-4">
                <p-panel data-tone="surface" header="Additional Items">
                  <p-card data-variant="compact">
                    @if (getManualItems().length > 0) {
                      <p-table class="manual-items-table cs-datatable-editable" [value]="getManualItems()" [scrollable]="true" scrollHeight="400px">
                        <ng-template pTemplate="header">
                          <tr>
                            <th class="col-item-code">Item Code</th>
                            <th class="col-item-description">Item Description</th>
                            <th class="col-unit">Unit</th>
                            <th class="col-actual-kg">Actual (Kg)</th>
                            <th>Notes</th>
                            <th class="col-action"></th>
                          </tr>
                        </ng-template>
                        <ng-template pTemplate="body" let-fg>
                          @for (entryFormGroup of actualValuesFormArray.controls; track $index; let i = $index) {
                            @if (entryFormGroup === fg) {
                              <tr [formGroupName]="i">
                                <td>
                                  <p-autocomplete
                                    [ngModel]="fg.controls.itemCode.value"
                                    [ngModelOptions]="{ standalone: true }"
                                    #itemSearch="cslServerSearch"
                                    [cslServerSearch]="fetchItems"
                                    [suggestions]="itemSearch.results()"
                                    optionLabel="displayLabel"
                                    [forceSelection]="true"
                                    [disabled]="recipeValues()!.status === RecipeValuesStatus.Completed || !canEdit()"
                                    styleClass="w-full"
                                    appendTo="body"
                                    (onSelect)="onItemSelected($event.value, fg)"
                                    (onClear)="fg.controls.itemCode.setValue(''); fg.controls.itemDescription.setValue(null); fg.controls.unit.setValue(null)"
                                  >
                                    <ng-template let-item pTemplate="item">
                                      <div class="text-sm">
                                        <b>{{ item.itemCode }}</b
                                        >&nbsp;{{ item.displayLabel | slice: item.itemCode.length + 3 }}
                                      </div>
                                    </ng-template>
                                  </p-autocomplete>
                                </td>
                                <td>{{ fg.controls.itemDescription.value || '--' }}</td>
                                <td>
                                  <p-select
                                    formControlName="unit"
                                    [options]="unitOptions"
                                    optionLabel="label"
                                    optionValue="value"
                                    [appendTo]="'body'"
                                    placeholder="Select unit"
                                    styleClass="w-full"
                                  />
                                </td>
                                <td>
                                  <input type="number" pInputText formControlName="actualKg" step="0.00001" min="0" class="w-full" />
                                </td>
                                <td>
                                  <input type="text" pInputText formControlName="notes" class="w-full" />
                                </td>
                                <td>
                                  <p-button
                                    icon="pi pi-trash"
                                    severity="danger"
                                    [text]="true"
                                    [disabled]="recipeValues()!.status === RecipeValuesStatus.Completed || !canEdit()"
                                    (onClick)="removeManualItem(fg)"
                                  />
                                </td>
                              </tr>
                            }
                          }
                        </ng-template>
                      </p-table>
                    }

                    @if (!(recipeValues()!.status === RecipeValuesStatus.Completed) && canEdit()) {
                      <div class="mt-2">
                        <p-button
                          label="Add Item"
                          icon="pi pi-plus"
                          severity="secondary"
                          [outlined]="true"
                          [disabled]="recipeValues()!.status === RecipeValuesStatus.Completed"
                          (onClick)="addManualItem()"
                        />
                      </div>
                    }
                  </p-card>
                </p-panel>
              </div>
            </div>
          </form>
        </p-panel>
      }
    </div>
  }
</main>
